import React, { useEffect } from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

import { Elements } from "@stripe/react-stripe-js";

import { loadStripe } from "@stripe/stripe-js";

import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { saveVisite } from "./services/visitesServices";

// Pages importées
import Profile from "./pages/Profile/Profile";
import HomePageVitrine from "./pages/Home/Home";
import LoginPage from "./pages/LoginPage/LoginPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";

import ResetPwd from "./pages/ResetPwd/ResetPwd";
import ForgotPasswordPage from "./pages/ResetPwd/ForgotPasswordPage";
import Desinscription from "./pages/Desinscription/Desinscription";

import MonAppliClub from "./pages/MonAppliClub/MonAppliClub";
import MonAppliClubLight from "./pages/MonAppliClubLight/MonAppliClubLight";
import MonAppliClubOmnisports from "./pages/MonAppliClubOmnisports/MonAppliClubOmnisports";
import MonAppliClubTournoi from "./pages/MonAppliClubTournoi/MonAppliClubTournoi";
import JudoCoachPro from "./pages/JudoCoachPro/JudoCoachPro";
import JudoPresencePro from "./pages/JudoPresencePro/JudoPresencePro";
import Tarifs from "./pages/Tarifs/Tarifs";

import PartenairesKataPage from "./pages/PartenairesKataPage/PartenairesKataPage.jsx";
import MonAppliCatePage from "./pages/MonAppliCatePage/MonAppliCatePage.jsx";
import MonAppliScorePage from "./pages/MonAppliScore/MonAppliScorePage";
import ScoreboardUserPage from "./pages/MonAppliScore/ScoreboardUserPage.jsx";
import MonAppliMairie from "./pages/MonAppliMairie/MonAppliMairie.jsx";

// RGPD
import ReglementInterieur from "./pages/Documents/ReglementInterieur";
import ConditionsUtilisationPage from "./pages/rgpd/ConditionsUtilisationPage.jsx";
import PolitiqueConfidentialitePage from "./pages/rgpd/PolitiqueConfidentialitePage.jsx";
import PolitiqueConfidentialitePageScoreboard from "./pages/rgpd/PolitiqueConfidentialitePageScoreboard.jsx";
import MentionsLegalesPage from "./pages/rgpd/MentionsLegalesPage.jsx";
import ReglesConfidentialitesTableauDeScoreJudo from "./pages/rgpd/ReglesConfidentialitesTableauDeScoreJudo.jsx";
import ConditionsGeneralesDeVente from "./pages/rgpd/ConditionsGeneralesDeVente.jsx";
import ErrorPage from "./pages/ErrorPage/ErrorPage";

// Application privée
import AsideMenu from "./pages/Appli/AsideMenu";
import MainContent from "./pages/Appli/MainContent";

// Context Providers
import { useUser, UserProvider } from "./contexts/UserContext";

import { NotificationProvider } from "./contexts/NotificationContext";

import { MessageProvider } from "./contexts/MessageContext";

import { ContactFormProvider } from "./contexts/ContactFormContext";

import { TaskProvider } from "./contexts/TaskContext.js";

import { AsideMenuProvider } from "./contexts/AsideMenuContext";

import PrimaryColorProvider from "./hooks/PrimaryColorProvider.jsx";

import initOneSignal from "./utils/OneSignalConfig.js";

import Navbar2 from "./pages/Home/Navbar2/Navbar2";

import VisitorTracking from "./components/VisitorTracking/VisitorTracking";

import VisitorContactWidget from "./components/VisitorContactWidget/VisitorContactWidget";

import { VisitorAdminAlertsProvider } from "./pages/Appli/AppliAdmin/VisitorCenter/context/VisitorAdminAlertsContext";

import "./App.css";

const stripePromise = loadStripe(
  "pk_test_51POgHVEr14Icaey6JeVMxJf9bAJS9AxDAGUTSDI80b01LVDorrxErNHC1HbTU3WRgJfPD3P40daULyEzeakVDBfk00fHPS7sVp",
);

const AppContent = () => {
  const { user } = useUser();

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Le résumé visiteur est chargé uniquement :
   * - dans l'application privée ;
   * - pour l'administrateur de rôle 1.
   */
  const visitorAlertsEnabled =
    location.pathname.startsWith("/appli") && Number(user?.role_id) === 1;

  const RequireAuth = ({ children, roles }) => {
    if (!user) {
      return <div>Chargement...</div>;
    }

    if (roles && !roles.includes(Number(user.role_id))) {
      return <Navigate to="/unauthorized" replace />;
    }

    return children;
  };

  useEffect(() => {
    initOneSignal();
  }, []);

  useEffect(() => {
    if (location.pathname === "/appli" && user) {
      if (Number(user.role_id) === 1) {
        navigate("/appli/dashboard-admin");
      } else if ([2, 3].includes(Number(user.role_id))) {
        navigate("/appli/notifications");
      }
    }
  }, [location.pathname, user, navigate]);

  /**
   * Ancien compteur de pages.
   *
   * Il reste actif uniquement sur le site public.
   * Les pages privées /appli ne doivent pas être comptabilisées.
   */
  useEffect(() => {
    if (location.pathname.startsWith("/appli")) {
      return;
    }

    const today = new Date().toISOString().slice(0, 10);

    const storageKey = `lastVisitDate_${location.pathname}`;

    const lastVisitDate = localStorage.getItem(storageKey);

    if (lastVisitDate !== today) {
      saveVisite({
        page: location.pathname,
      });

      localStorage.setItem(storageKey, today);
    }
  }, [location.pathname]);

  return (
    <VisitorAdminAlertsProvider enabled={visitorAlertsEnabled} pollMs={8000}>
      <div className="flex min-h-screen flex-col">
        <VisitorTracking />

        <VisitorContactWidget />

        {!location.pathname.startsWith("/appli") && <Navbar2 user={user} />}

        <Routes>
          <Route path="/" element={<HomePageVitrine />} />

          <Route
            path="/profile"
            element={
              <RequireAuth roles={[1, 2, 3]}>
                <Profile />
              </RequireAuth>
            }
          />

          <Route path="/login" element={<LoginPage />} />

          <Route path="/register" element={<RegisterPage />} />

          <Route path="/reset-password/:token" element={<ResetPwd />} />

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          <Route path="/desinscription" element={<Desinscription />} />

          {/* Application privée */}
          <Route
            path="/appli/*"
            element={
              <RequireAuth roles={[1, 2, 3]}>
                <div className="flex">
                  <AsideMenu role_id={user?.role_id} userInfo={user} />

                  <MainContent role_id={user?.role_id} />
                </div>
              </RequireAuth>
            }
          />

          {/* Pages produits */}
          <Route path="/product-monappliclub" element={<MonAppliClub />} />

          <Route
            path="/product-monappliclub-light"
            element={<MonAppliClubLight />}
          />

          <Route path="/product-monapplimairie" element={<MonAppliMairie />} />

          <Route
            path="/product-monappliclub-omnisports"
            element={<MonAppliClubOmnisports />}
          />

          <Route
            path="/product-monappliclub-tournoi"
            element={<MonAppliClubTournoi />}
          />

          <Route path="/product-judocoachpro" element={<JudoCoachPro />} />

          <Route
            path="/product-judopresencepro"
            element={<JudoPresencePro />}
          />

          <Route path="/tarifs" element={<Tarifs />} />

          <Route path="/partenaires-kata" element={<PartenairesKataPage />} />

          <Route path="/monapplicate" element={<MonAppliCatePage />} />

          <Route path="/monappliscore" element={<MonAppliScorePage />} />
          <Route
            path="/monappliscore/tableau/:sessionId"
            element={<ScoreboardUserPage />}
          />

          {/* RGPD et documents */}
          <Route path="/reglement-interieur" element={<ReglementInterieur />} />

          <Route
            path="/conditions-utilisation"
            element={<ConditionsUtilisationPage />}
          />

          <Route
            path="/politique-confidentialite"
            element={<PolitiqueConfidentialitePage />}
          />

          <Route
            path="/politique-confidentialite-tableau-de-score-judo"
            element={<PolitiqueConfidentialitePageScoreboard />}
          />

          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />

          <Route
            path="/regles-confidentialites-tableau-de-score-judo"
            element={<ReglesConfidentialitesTableauDeScoreJudo />}
          />

          <Route
            path="/conditions-generales-de-vente"
            element={<ConditionsGeneralesDeVente />}
          />

          <Route path="/404" element={<ErrorPage />} />

          <Route path="*" element={<ErrorPage />} />
        </Routes>

        {/*
         * Un seul ToastContainer global suffit pour afficher
         * les alertes dans toute l'application.
         */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </div>
    </VisitorAdminAlertsProvider>
  );
};

const App = () => {
  return (
    <UserProvider>
      <Router>
        <Elements stripe={stripePromise}>
          <MessageProvider>
            <NotificationProvider>
              <ContactFormProvider>
                <TaskProvider>
                  <AsideMenuProvider>
                    <PrimaryColorProvider>
                      <div className="flex min-h-screen flex-col bg-base-200 dark:bg-gray-600 dark:text-gray-200">
                        <AppContent />
                      </div>
                    </PrimaryColorProvider>
                  </AsideMenuProvider>
                </TaskProvider>
              </ContactFormProvider>
            </NotificationProvider>
          </MessageProvider>
        </Elements>
      </Router>
    </UserProvider>
  );
};

export default App;
